import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Dialog } from 'primereact/dialog';
import { Sidebar } from 'primereact/sidebar';
import { Menu, Settings2, Plus, Minus } from 'lucide-react';
import { useLabelStore } from "../../../store/labelStore";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { LabelCategory, PrinterLanguage } from "../../../types/label";
import { TopBar } from "../../../components/designer/TopBar";
import { LeftPanel } from "../../../components/designer/LeftPanel";
import { Canvas } from "../../../components/designer/Canvas";
import { RightPanel } from "../../../components/designer/RightPanel";
import { CanvasElement, ElementType } from "../../../components/designer/types";

import { useHistory } from "../../../hooks/useHistory";

export const LabelDesigner = () => {
    const { templateId } = useParams<{ templateId: string }>();
    const { handleApiError } = useApiErrorHandler();
    const { getTemplateById, saveVariant, getVariablesByCategory, getSampleData } = useLabelStore();

    const [template, setTemplate] = useState<any>(null);
    const [category, setCategory] = useState<LabelCategory | null>(null);
    const [widthMm, setWidthMm] = useState<number>(100);
    const [heightMm, setHeightMm] = useState<number>(100);
    const [language, setLanguage] = useState<PrinterLanguage>("ZPL");

    const { state: elements, setState: setElements, undo, redo, canUndo, canRedo, resetState: resetElements } = useHistory<CanvasElement[]>([]);
    const [calculatedVariables, setCalculatedVariables] = useState<any[]>([]);
    const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
    const [variables, setVariables] = useState<any[]>([]);
    const [sampleData, setSampleData] = useState<Record<string, any>>({});
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(false);
    const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
    const [zoom, setZoom] = useState<number>(1);
    const [previewZoom, setPreviewZoom] = useState<number>(1);
    const [isLeftPinned, setIsLeftPinned] = useState(false);
    const [isRightPinned, setIsRightPinned] = useState(false);

    useEffect(() => {
        if (templateId) {
            loadTemplateData();
        }
    }, [templateId]);

    useEffect(() => {
        if (category) {
            loadVariables(category);
        }
    }, [category]);

    const loadTemplateData = async () => {
        try {
            const data = await getTemplateById(templateId!);
            setTemplate(data);

            // Eğer category set edilmemişse veya OTHER ise, code alanını category olarak kullan (geriye dönük uyumluluk)
            const resolvedCategory = (data.category && data.category !== 'OTHER') ? data.category : data.code;
            if (resolvedCategory) {
                setCategory(resolvedCategory as LabelCategory);
            }

            // Load first variant if exists
            const variant = data.variants && data.variants.length > 0 ? data.variants[0] : null;
            if (variant) {
                setWidthMm(variant.widthMm || 100);
                setHeightMm(variant.heightMm || 100);
                setLanguage(variant.language || "ZPL");

                try {
                    const parsedContent = JSON.parse(variant.content);
                    if (Array.isArray(parsedContent)) {
                        resetElements(parsedContent);
                        setCalculatedVariables([]);
                    } else if (parsedContent) {
                        resetElements(parsedContent.elements || []);
                        setCalculatedVariables(parsedContent.calculatedVariables || []);
                    }
                } catch (e) {
                    console.warn("Could not parse existing variant content as JSON. Starting fresh.");
                }
            } else {
                setWidthMm(100);
                setHeightMm(100);
            }

        } catch (error) {
            handleApiError(error);
        }
    };

    const loadVariables = async (cat: LabelCategory) => {
        const vars = await getVariablesByCategory(cat);
        setVariables(vars || []);

        const validKeys = (vars || []).map((v: any) => v.key || v.name || v);

        setElements(prev => prev.map(el => {
            if (!el.content || typeof el.content !== 'string') return el;
            const regex = /\{\{([^}]+)\}\}/g;
            let newContent = el.content.replace(regex, (match, p1) => {
                if (validKeys.includes(p1.trim())) {
                    return match;
                }
                return ''; // remove invalid variable
            });
            newContent = newContent.replace(/\s{2,}/g, ' ').trim();
            return { ...el, content: newContent };
        }));
    };

    const handleSave = async () => {
        try {
            const contentObj = { elements, calculatedVariables };
            const contentJson = JSON.stringify(contentObj);
            await saveVariant({
                templateId: Number(templateId),
                language: language,
                widthMm: widthMm,
                heightMm: heightMm,
                content: contentJson
            });
            toast.success("Tasarım başarıyla kaydedildi.");
        } catch (error) {
            handleApiError(error);
        }
    };

    const handlePreview = async () => {
        if (category) {
            const data = await getSampleData(category);
            if (data) setSampleData(data);
        }
        setIsPreviewOpen(true);
    };

    const handleAddElement = (type: ElementType, initialContent?: string) => {
        const newElement: CanvasElement = {
            id: Math.random().toString(36).substring(7),
            type,
            content: initialContent || (type === 'TEXT' ? 'Yeni Metin' : type === 'BARCODE' ? '123456789' : type === 'QR' ? 'https://plasiyo.com' : ''),
            x: 10,
            y: 10,
            width: type === 'VLINE' ? 1 : (type !== 'TEXT' && type !== 'LINE' ? 50 : undefined),
            height: type === 'LINE' ? 1 : (type === 'VLINE' ? 20 : (type !== 'TEXT' ? 20 : undefined)),
            fontSize: type === 'TEXT' ? 12 : undefined,
            fontWeight: 'normal',
            borderWidth: type === 'BOX' || type === 'LINE' || type === 'VLINE' ? 2 : undefined,
            borderStyle: type === 'BOX' || type === 'LINE' || type === 'VLINE' ? 'solid' : undefined
        };
        setElements([...elements, newElement]);
        setSelectedElementId(newElement.id);
    };

    const handleAddVariable = (variableStr: string) => {
        if (selectedElementId) {
            setElements(prev => prev.map(el => {
                if (el.id === selectedElementId && (el.type === 'TEXT' || el.type === 'BARCODE' || el.type === 'QR')) {
                    return { ...el, content: el.content + ' ' + variableStr };
                }
                return el;
            }));
        }
    };

    const updateElement = (id: string, updates: Partial<CanvasElement>) => {
        setElements(prev => prev.map(el => el.id === id ? { ...el, ...updates } : el));
    };

    const deleteElement = (id: string) => {
        setElements(prev => prev.filter(el => el.id !== id));
        if (selectedElementId === id) {
            setSelectedElementId(null);
        }
    };

    const moveElementLayer = (id: string, direction: 'up' | 'down') => {
        setElements(prev => {
            const index = prev.findIndex(el => el.id === id);
            if (index < 0) return prev;

            const newElements = [...prev];
            if (direction === 'up' && index < newElements.length - 1) {
                [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
            } else if (direction === 'down' && index > 0) {
                [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
            }
            return newElements;
        });
    };

    const selectedElement = elements.find(el => el.id === selectedElementId);

    return (
        <div className="flex flex-col gap-4 h-[calc(100vh-120px)]">
            <TopBar
                templateName={template?.name}
                templateCode={template?.code}
                category={category}
                setCategory={setCategory}
                widthMm={widthMm}
                setWidthMm={setWidthMm}
                heightMm={heightMm}
                setHeightMm={setHeightMm}
                onSave={handleSave}
                onPreview={handlePreview}
                onUndo={undo}
                onRedo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
            />

            <div className="flex flex-1 gap-4 overflow-hidden relative">
                {/* Desktop Left Panel */}
                <div className={`hidden lg:block h-full relative z-20 shrink-0 transition-all duration-300 ${isLeftPinned ? 'w-72' : 'w-16'}`}>
                    <div className={`absolute top-0 left-0 h-full transition-all duration-300 ${isLeftPinned ? 'w-72' : 'w-16 hover:w-72 group'}`}>
                        <LeftPanel
                            onAddElement={handleAddElement}
                            variables={variables}
                            onAddVariable={handleAddVariable}
                            selectedElementId={selectedElementId}
                            calculatedVariables={calculatedVariables}
                            onSaveCalculatedVariable={(v) => {
                                setCalculatedVariables(prev => {
                                    const exists = prev.findIndex(p => p.name === v.name);
                                    if (exists >= 0) {
                                        const newArr = [...prev];
                                        newArr[exists] = v;
                                        return newArr;
                                    }
                                    return [...prev, v];
                                });
                            }}
                            isPinned={isLeftPinned}
                            onTogglePin={() => setIsLeftPinned(!isLeftPinned)}
                        />
                    </div>
                </div>

                {/* Mobile Left Sidebar */}
                <Sidebar visible={isLeftSidebarOpen} onHide={() => setIsLeftSidebarOpen(false)} position="left" className="w-72 p-0">
                    <LeftPanel
                        onAddElement={handleAddElement}
                        variables={variables}
                        onAddVariable={handleAddVariable}
                        selectedElementId={selectedElementId}
                        calculatedVariables={calculatedVariables}
                        onSaveCalculatedVariable={(v) => {
                            setCalculatedVariables(prev => {
                                const exists = prev.findIndex(p => p.name === v.name);
                                if (exists >= 0) {
                                    const newArr = [...prev];
                                    newArr[exists] = v;
                                    return newArr;
                                }
                                return [...prev, v];
                            });
                        }}
                    />
                </Sidebar>

                {/* Canvas Container */}
                <div className="flex-1 flex flex-col h-full overflow-hidden relative">
                    <Canvas
                        elements={elements}
                        setElements={setElements}
                        selectedElementId={selectedElementId}
                        setSelectedElementId={setSelectedElementId}
                        widthMm={widthMm}
                        heightMm={heightMm}
                        zoom={zoom}
                        calculatedVariables={calculatedVariables}
                    />

                    {/* Zoom Controls */}
                    <div className="absolute top-4 right-4 hidden lg:flex bg-card border border-borderColor rounded-lg shadow-sm overflow-hidden z-10">
                        <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} className="p-2 hover:bg-bgAlt"><Minus className="w-4 h-4" /></button>
                        <span className="p-2 text-sm font-medium w-16 text-center">{Math.round(zoom * 100)}%</span>
                        <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} className="p-2 hover:bg-bgAlt"><Plus className="w-4 h-4" /></button>
                    </div>

                    {/* Mobile Floating Buttons */}
                    <div className="lg:hidden absolute bottom-4 left-4 right-4 flex justify-between z-10 pointer-events-none">
                        <button
                            onClick={() => setIsLeftSidebarOpen(true)}
                            className="bg-primary text-white p-3 rounded-full shadow-lg pointer-events-auto hover:bg-primaryHover"
                        >
                            <Menu className="w-6 h-6" />
                        </button>
                        <button
                            onClick={() => setIsRightSidebarOpen(true)}
                            className="bg-secondary text-white p-3 rounded-full shadow-lg pointer-events-auto hover:bg-secondaryHover"
                        >
                            <Settings2 className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <div className={`hidden lg:block h-full relative z-20 shrink-0 transition-all duration-300 ${isRightPinned ? 'w-80' : 'w-16'}`}>
                    <div className={`absolute top-0 right-0 h-full transition-all duration-300 ${isRightPinned ? 'w-80' : 'w-16 hover:w-80 group'}`}>
                        <RightPanel
                            selectedElement={selectedElement}
                            updateElement={updateElement}
                            deleteElement={deleteElement}
                            variables={variables}
                            moveElementLayer={moveElementLayer}
                            isPinned={isRightPinned}
                            onTogglePin={() => setIsRightPinned(!isRightPinned)}
                            elements={elements}
                            setSelectedElementId={setSelectedElementId}
                        />
                    </div>
                </div>

                <Sidebar visible={isRightSidebarOpen} onHide={() => setIsRightSidebarOpen(false)} position="right" className="w-80 p-0">
                    <RightPanel
                        selectedElement={selectedElement}
                        updateElement={updateElement}
                        deleteElement={deleteElement}
                        variables={variables}
                        moveElementLayer={moveElementLayer}
                    />
                </Sidebar>
            </div>

            <Dialog
                visible={isPreviewOpen}
                onHide={() => setIsPreviewOpen(false)}
                header={
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full pr-6">
                        <h3 className="font-bold text-lg text-text m-0 truncate max-w-full sm:max-w-[60%]">
                            {template?.name ? `${template.name} Önizleme` : 'Tasarım Önizleme'}
                        </h3>
                        <div className="flex items-center bg-bgAlt border border-borderColor rounded-md shadow-sm shrink-0">
                            <button onClick={() => setPreviewZoom(z => Math.max(0.25, z - 0.25))} className="p-2 hover:bg-borderColor text-text/70 hover:text-text transition-colors rounded-l-md">
                                <Minus className="w-4 h-4" />
                            </button>
                            <div className="w-14 text-center text-sm font-bold text-text border-x border-borderColor py-1">
                                {Math.round(previewZoom * 100)}%
                            </div>
                            <button onClick={() => setPreviewZoom(z => Math.min(3, z + 0.25))} className="p-2 hover:bg-borderColor text-text/70 hover:text-text transition-colors rounded-r-md">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                }
                className="w-[90vw] md:w-[70vw] lg:w-[50vw]"
                style={{ minHeight: '60vh' }}
            >
                <div className="flex flex-col h-full">

                    <div className="flex items-center justify-center p-4 max-h-[75vh] overflow-auto">
                        <Canvas
                            elements={elements}
                            setElements={() => { }}
                            selectedElementId={null}
                            setSelectedElementId={() => { }}
                            widthMm={widthMm}
                            heightMm={heightMm}
                            readOnly={true}
                            sampleData={sampleData}
                            zoom={previewZoom}
                            calculatedVariables={calculatedVariables}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};